'use client'

// The homepage's Alkanes markets: AMM data, not the marketplace orderbook —
// alkanes have no listings, floor or volume there. Same per-asset pool feed
// the /alkanes terminal and /tokens read; every row opens its trading page.

import Link from 'next/link'
import useSWR from 'swr'
import { ArrowRight } from 'lucide-react'
import { API_URL } from '@/utils/constants'
import { formatUsdPrice, usdCompact } from '@/app/alkanes/lib/format'
import type { AssetRow } from '@/app/alkanes/lib/useAssets'

const ROWS = 10

export function useAlkaneMarkets(enabled: boolean) {
	const { data, error, isLoading } = useSWR<{ assets?: AssetRow[] }>(
		enabled ? `${API_URL}/alkane/assets` : null,
		async (url: string) => {
			const response = await fetch(url)
			if (!response.ok) throw new Error('Alkanes markets unavailable')
			return response.json()
		},
		{ refreshInterval: 60_000, dedupingInterval: 30_000, refreshWhenHidden: false }
	)
	const markets = (data?.assets ?? [])
		// frBTC is the wrapped-BTC quote asset, not a market of its own.
		.filter((asset) => asset.id !== '32:0' && (asset.volume_24h_usd ?? 0) > 0)
		// Not `toSorted`: Chrome 109 (the last build for Windows 7/8) lacks it
		// and crashed the home page.
		.sort((a, b) => (b.volume_24h_usd ?? 0) - (a.volume_24h_usd ?? 0))
		.slice(0, ROWS)
	return {
		markets,
		volumeUsd: markets.reduce((sum, asset) => sum + (asset.volume_24h_usd ?? 0), 0),
		// Not summed liquidity: pools are two-sided, so per-token depth added
		// together would count every shared pool twice.
		liveMarkets: (data?.assets ?? []).filter((asset) => asset.id !== '32:0').length,
		error,
		isLoading
	}
}

export default function AlkaneMarkets({
	markets,
	error,
	isLoading
}: Pick<ReturnType<typeof useAlkaneMarkets>, 'markets' | 'error' | 'isLoading'>) {
	return (
		<div className="overflow-x-auto border-t border-border/70" aria-busy={isLoading}>
			<table className="w-full text-left text-xs" data-testid="home-alkane-markets">
				<caption className="sr-only">
					Alkanes markets ranked by 24 hour AMM trading volume. Trading links open the Alkanes terminal.
				</caption>
				<thead className="market-column-header border-b border-border/70 bg-muted/15 text-[10px] text-muted-foreground">
					<tr>
						<th className="px-4 py-3">#</th>
						<th className="px-2 py-3">Alkane</th>
						<th className="hidden px-3 py-3 text-right md:table-cell">Price</th>
						<th className="hidden px-3 py-3 text-right lg:table-cell">24h change</th>
						<th className="px-3 py-3 text-right">24h volume</th>
						<th className="hidden px-3 py-3 text-right xl:table-cell">Liquidity</th>
						<th className="hidden px-3 py-3 text-right xl:table-cell">Market cap</th>
						<th className="hidden px-4 py-3 sm:table-cell">
							<span className="sr-only">Trade on OW</span>
						</th>
					</tr>
				</thead>
				<tbody>
					{markets.map((row, index) => {
						const change = row.change_24h_pct
						const href = `/alkanes/${row.id}`
						return (
							<tr key={row.id} className="border-b border-border/50 last:border-0 hover:bg-muted/20">
								<td className="px-4 py-3 text-muted-foreground">{index + 1}</td>
								<td className="px-2 py-3">
									<div className="flex items-center gap-2.5">
										{row.icon ? (
											<img
												src={row.icon}
												alt=""
												loading="lazy"
												className="size-9 shrink-0 rounded-lg object-cover"
											/>
										) : (
											<span
												className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-purple-500/20 to-blue-500/10 text-[10px] font-bold"
												aria-hidden="true"
											>
												{(row.name || row.id).slice(0, 2).toUpperCase()}
											</span>
										)}
										<div>
											<Link
												href={href}
												prefetch={false}
												className="block max-w-[130px] truncate font-semibold hover:text-purple-500 sm:max-w-[200px]"
												title={row.name || row.id}
											>
												{row.name || row.id}
											</Link>
											<span className="text-[10px] text-muted-foreground">{row.id}</span>
										</div>
									</div>
								</td>
								<td className="hidden whitespace-nowrap px-3 py-3 text-right tabular-nums md:table-cell">
									{row.price_usd !== null ? formatUsdPrice(row.price_usd) : '—'}
								</td>
								<td
									className={`hidden whitespace-nowrap px-3 py-3 text-right tabular-nums lg:table-cell ${
										typeof change === 'number' && change > 0
											? 'text-emerald-600 dark:text-emerald-400'
											: typeof change === 'number' && change < 0
											? 'text-red-500'
											: 'text-muted-foreground'
									}`}
								>
									{typeof change === 'number' ? `${change > 0 ? '+' : ''}${change.toFixed(2)}%` : '—'}
								</td>
								<td className="whitespace-nowrap px-3 py-3 text-right font-semibold tabular-nums">
									{usdCompact(row.volume_24h_usd)}
								</td>
								<td className="hidden whitespace-nowrap px-3 py-3 text-right tabular-nums xl:table-cell">
									{usdCompact(row.liquidity_usd)}
								</td>
								<td className="hidden whitespace-nowrap px-3 py-3 text-right tabular-nums xl:table-cell">
									{row.market_cap_usd ? usdCompact(row.market_cap_usd) : '—'}
								</td>
								<td className="hidden px-4 py-3 sm:table-cell">
									<Link
										href={href}
										prefetch={false}
										aria-label={`Trade ${row.name || row.id} on Ordinals Wallet`}
										className="inline-flex min-h-10 items-center gap-1.5 whitespace-nowrap font-semibold text-purple-600 underline-offset-4 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-ring dark:text-purple-400"
									>
										Trade
										<ArrowRight className="size-3" strokeWidth={1.5} aria-hidden="true" />
									</Link>
								</td>
							</tr>
						)
					})}
				</tbody>
			</table>
			{!markets.length && (
				<p className="px-6 py-10 text-sm text-muted-foreground" role="status">
					{error ? 'Alkanes market data is temporarily unavailable.' : 'Loading Alkanes markets…'}
				</p>
			)}
		</div>
	)
}
